import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { generatePath, useNavigate } from 'react-router';
import type { DentalOfficeShiftCardMenuItem } from '@tempmee/react-blocks';
import type { IInterviewInviteTarget } from '~/components/counterJobs/OpenJobScreenRedesign/useInterviewInvite';
import { useCancelJobMutation } from '~/domain/Jobs';
import {
  IGetOnsiteInterviewListViewEntity,
  OnsiteInterviewStatusMapper,
} from '~/domain/PermanentJobs';
import { routes } from '~/routes';
import { invalidatePostingLists } from '../../../utils';
import { interviewCardActions } from '../../postingDetailsRules';

export interface IUsePostingInterviewCardArgs {
  interview: IGetOnsiteInterviewListViewEntity;
  jobPostingId: string;
  /** The posting's office, which the invite is sent on behalf of. */
  officeId?: string;
}

/**
 * An interview's `localDate` ("Thu, Sep 3, 2026", the office's day) as the `YYYY-MM-DD`
 * the eligible-candidates request scopes availability by.
 */
export const toInviteDate = (localDate: string): string | undefined => {
  const parsed = moment(localDate, 'ddd, MMM D, YYYY');
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : undefined;
};

/**
 * Drives one Interviews-tab card: the `⋯` menu the dashboard's shift lists give an
 * interview (Message professional when scheduled, Edit Interview when open, Cancel
 * Interview until it happens), the envelope that invites the posting's applicants to
 * an open interview, and the state of the invite, invite-sent and cancel popups. A
 * cancel refetches the posting's lists, since the page stays mounted.
 */
export const usePostingInterviewCard = ({
  interview,
  jobPostingId,
  officeId,
}: IUsePostingInterviewCardArgs) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isInviteOpen, setInviteOpen] = useState(false);
  const [isInviteSentOpen, setInviteSentOpen] = useState(false);
  const [isCancelOpen, setCancelOpen] = useState(false);
  const { mutate: cancelJob, isPending: isCancelPending } = useCancelJobMutation();

  const actions = interviewCardActions(OnsiteInterviewStatusMapper.fromJobStatus(interview.status));
  const jobId = interview.id;

  const menuItems: DentalOfficeShiftCardMenuItem[] = [];
  if (actions.message) {
    menuItems.push({
      label: 'Message professional',
      onSelect: () => navigate(generatePath(routes.chat, { jobId })),
      testID: 'posting-interview-menu-message',
    });
  }
  if (actions.edit) {
    menuItems.push({
      label: 'Edit Interview',
      onSelect: () => navigate(generatePath(routes.editOpenJob, { jobId })),
      testID: 'posting-interview-menu-edit',
    });
  }
  if (actions.cancel) {
    menuItems.push({
      label: 'Cancel Interview',
      onSelect: () => setCancelOpen(true),
      testID: 'posting-interview-menu-cancel',
    });
  }

  const inviteTarget: IInterviewInviteTarget = {
    postingId: jobPostingId,
    interviewId: jobId,
    officeId,
    date: toInviteDate(interview.localDate),
  };

  return {
    menuItems: menuItems.length > 0 ? menuItems : undefined,
    openInvite: actions.invite ? () => setInviteOpen(true) : undefined,
    inviteTarget,
    isInviteOpen,
    closeInvite: () => setInviteOpen(false),
    // The invite dialog closes on a send, and the confirmation takes its place.
    handleInvited: () => {
      setInviteOpen(false);
      setInviteSentOpen(true);
    },
    isInviteSentOpen,
    closeInviteSent: () => setInviteSentOpen(false),
    isCancelOpen,
    closeCancel: () => setCancelOpen(false),
    confirmCancel: () => {
      setCancelOpen(false);
      cancelJob({ jobId }, { onSuccess: () => invalidatePostingLists(queryClient, jobPostingId) });
    },
    isCancelPending,
  };
};
